import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Header } from '@/components/Header';
import { Footer } from '@/components/Footer';
import { ProductCard } from '@/components/ProductCard';
import { GiftHampersSection } from '@/components/GiftHampersSection';
import { HamperOptionsSection } from '@/components/HamperOptionsSection';
import { Button } from '@/components/ui/button';
import { Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Products = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState(searchParams.get('category') || 'all');

  const categories = [
    { id: 'all', name: 'All Products' },
    { id: 'breads', name: 'Breads' },
    { id: 'sourdough', name: 'Sourdough Breads' },
    { id: 'cookies', name: 'Cookies' },
    { id: 'tea-cakes', name: 'Tea Cakes' },
    { id: 'cakes', name: 'Cakes' },
    { id: 'sweet-bites', name: 'Sweet Bites' },
    { id: 'savoury-bites', name: 'Savoury Bites' },
    { id: 'hampers', name: 'Gift Hampers' }
  ];

  useEffect(() => {
    const category = searchParams.get('category');
    const search = searchParams.get('search');
    if (category) setSelectedCategory(category);
    fetchProducts(category || 'all', search);
  }, [searchParams]);

  const fetchProducts = async (category, search) => {
    setLoading(true);
    try {
      const params = {};
      if (category && category !== 'all') params.category = category;
      if (search) params.search = search;
      
      const response = await axios.get(`${API}/products`, { params });
      setProducts(response.data);
    } catch (error) {
      console.error('Failed to fetch products:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCategoryChange = (category) => {
    // Redirect to Custom Cakes page when Cakes category is clicked
    if (category === 'cakes') {
      navigate('/custom-cakes');
      return;
    }
    
    setSelectedCategory(category);
    const newParams = new URLSearchParams(searchParams);
    if (category === 'all') {
      newParams.delete('category');
    } else {
      newParams.set('category', category);
    }
    setSearchParams(newParams);
  };

  const handleSearch = (query) => {
    const newParams = new URLSearchParams(searchParams);
    if (query) {
      newParams.set('search', query);
    } else {
      newParams.delete('search');
    }
    setSearchParams(newParams);
  };

  return (
    <div className="min-h-screen flex flex-col">
      <Header onSearch={handleSearch} />
      
      <div className="flex-1 bg-gradient-to-b from-amber-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <h1 className="text-3xl md:text-4xl font-bold text-accent mb-6" data-testid="products-title">
            Our Products
          </h1>

          <div className="flex flex-wrap gap-2 mb-8 overflow-x-auto pb-4" data-testid="category-filters">
            {categories.map((category) => (
              <Button
                key={category.id}
                variant={selectedCategory === category.id ? 'default' : 'outline'}
                onClick={() => handleCategoryChange(category.id)}
                className={`rounded-full ${
                  selectedCategory === category.id
                    ? 'bg-primary hover:bg-primary/90'
                    : 'border-amber-200 hover:bg-amber-50'
                }`}
                data-testid={`category-filter-${category.id}`}
              >
                {category.name}
              </Button>
            ))}
          </div>

          {selectedCategory === 'hampers' ? (
            <HamperOptionsSection />
          ) : loading ? (
            <div className="flex justify-center items-center py-20" data-testid="loading-spinner">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          ) : products.length === 0 ? (
            <div className="text-center py-20" data-testid="no-products">
              <p className="text-muted-foreground text-lg">No products found</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-8" data-testid="products-grid">
              {products.map((product) => (
                <ProductCard
                  key={product.id}
                  product={product}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default Products;
